import 'react-responsive-carousel/lib/styles/carousel.min.css';
import * as apiValues from '../constants/apiValues';
import { MAP_IFRAME_TITLE } from '../constants/mapAccessibility';
import * as helpers from '../helpers';
import {wrapMarkdownIframes} from '../utils/markdownEmbeds';
import {applyWheelScrollToWindow} from '../utils/pageScrollFromWheel';
import {passPointerThroughMapShield} from '../utils/mapScrollShield';
import {resolveProjectFeatureForType} from '../utils/projectFeatureMatch';
import {resolveProjectPhaseLabel} from '../utils/projectStatusLabel';
import {
  collectIntroLineBottoms,
  introClampMaxHeightPx,
} from '../utils/introClamp';
import {applyBalancedDotRows} from '../utils/balanceDotRows';
import {
  PROJECT_PHOTOS_DESKTOP_SLIDE_PCT,
  PROJECT_PHOTOS_MOBILE_SLIDE_PCT,
  createProjectPhotosWrapSnapper,
  projectPhotosAnimationHandler as runProjectPhotosAnimationHandler,
  projectPhotosStopSwipingHandler as runProjectPhotosStopSwipingHandler,
  projectPhotosSwipeAnimationHandler as runProjectPhotosSwipeAnimationHandler,
} from '../utils/projectPhotosCarousel';
import * as pageProjectDetailActions from '../actions/pageProjectDetailActions';
import DocumentTitle from './DocumentTitle';
import iconBack from '../img/icons/back-arrow.svg';
import iconCaretRight from '../img/icons/icon-caret-right.svg';
import iconExpandMap from '../img/icons/icon-expand-map.svg';
import loadingIcon from '../img/loading-icon-small.gif';
import Lightbox from './Lightbox';
import MarkdownField from './MarkdownField';
import MBreadcrumbs from './MBreadcrumbs';
import MListTag from './MListTag';
import MNewsletter from './MNewsletter';
import NewsItem from './NewsItem';
import EventItem from './EventItem';
import PageBase from './PageBase';
import ProgramBanner from './ProgramBanner';
import PropTypes from 'prop-types';
import React from 'react';
import {Carousel} from 'react-responsive-carousel';
import SuperLink from './SuperLink';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from '../withRouterCompat';

export const MOBILE_SHEET_DETENTS = [20, 40, 60];
export const MOBILE_SHEET_DEFAULT_DETENT = 40;
export const MOBILE_MAP_SHEET_GAP_PX = 12;
const MOBILE_SHEET_HARD_MIN = 12;
const MOBILE_SHEET_HARD_MAX = 72;
const MOBILE_SHEET_RESISTANCE = 0.35;

export function getMobileSheetHeightStyle(pct) {
  return `${pct}%`;
}

export function getMobileSheetLegendBottomStyle(pct) {
  return `calc(${pct}% + ${MOBILE_MAP_SHEET_GAP_PX}px)`;
}

export function applySheetResistance(pct) {
  const value = Number(pct);
  if(!Number.isFinite(value)) return MOBILE_SHEET_DEFAULT_DETENT;
  const min = MOBILE_SHEET_DETENTS[0];
  const max = MOBILE_SHEET_DETENTS[MOBILE_SHEET_DETENTS.length - 1];
  let next = value;
  if(value < min) next = min - (min - value) * MOBILE_SHEET_RESISTANCE;
  else if(value > max) next = max + (value - max) * MOBILE_SHEET_RESISTANCE;
  return Math.min(MOBILE_SHEET_HARD_MAX, Math.max(MOBILE_SHEET_HARD_MIN, next));
}

export function snapSheetDetent(pct) {
  const value = Number(pct);
  if(!Number.isFinite(value)) return MOBILE_SHEET_DEFAULT_DETENT;
  return MOBILE_SHEET_DETENTS.reduce((best, detent) => (
    Math.abs(detent - value) < Math.abs(best - value) ? detent : best
  ));
}

function trimProjectMarkup(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function joinProjectAboutMarkup(page) {
  if(!page) return '';
  return [
    trimProjectMarkup(page.whatDescription),
    trimProjectMarkup(page.whyDescription),
    trimProjectMarkup(page.description),
  ].filter(Boolean).join('\n\n');
}

export function mergeProjectIntroMarkup(page) {
  if(!page) return '';
  const intro = trimProjectMarkup(page.introDescription);
  if(intro) return intro;
  return joinProjectAboutMarkup(page);
}

function decodeDocumentHtml(value) {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function stripDocumentMarkup(value) {
  return decodeDocumentHtml(value).replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

export function parseProjectDocumentLinks(markup) {
  if(!markup || typeof markup !== 'string') return [];

  const links = [];
  const seen = new Set();
  const addLink = (title, url) => {
    const cleanTitle = stripDocumentMarkup(title);
    const cleanUrl = decodeDocumentHtml(url).trim();
    if(!cleanTitle || !cleanUrl) return;
    const key = `${cleanUrl}|${cleanTitle}`;
    if(seen.has(key)) return;
    seen.add(key);
    links.push({title: cleanTitle, url: cleanUrl});
  };

  const htmlRe = /<a\s+[^>]*href\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi;
  let match;
  while((match = htmlRe.exec(markup))) {
    addLink(match[3], match[2]);
  }
  if(links.length > 0) return links;

  const mdRe = /(!?)\[([^\]]+)\]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;
  while((match = mdRe.exec(markup))) {
    if(match[1] === '!') continue;
    addLink(match[2], match[3]);
  }

  return links;
}

export function resolveProjectDocumentUrl(url) {
  return decodeDocumentHtml(url).replace(/(?:\[cms]|\[CMS])/g, () => apiValues.getCmsUploadBase());
}

function contactMarkupToLines(markup) {
  return decodeDocumentHtml(markup)
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*(p|div|li|h[1-6])[^>]*>/gi, '')
    .replace(/<\/?\s*(ul|ol)[^>]*>/gi, '\n')
    .split(/\r\n|\r|\n/)
    .map(line => line.replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '').trim())
    .filter(line => line.length > 0);
}

function parseContactLine(line) {
  const html = /^\s*<a\s+[^>]*href\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>\s*$/i.exec(line);
  if(html){
    return {
      type: 'link',
      title: stripDocumentMarkup(html[3]),
      url: decodeDocumentHtml(html[2]).trim(),
    };
  }

  const md = /^(!?)\[([^\]]+)\]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)\s*$/.exec(line);
  if(md && md[1] !== '!'){
    return {
      type: 'link',
      title: stripDocumentMarkup(md[2]),
      url: decodeDocumentHtml(md[3]).trim(),
    };
  }

  if(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(line)){
    return {type: 'link', title: line, url: `mailto:${line}`};
  }

  if(/^(https?:\/\/|www\.)/i.test(line)){
    const url = /^www\./i.test(line) ? `https://${line}` : line;
    return {type: 'link', title: line.replace(/^https?:\/\//i, ''), url};
  }

  return {type: 'text', value: stripDocumentMarkup(line)};
}

export function parseProjectContactBlocks(markup) {
  if(!markup || typeof markup !== 'string') return [];

  const lines = contactMarkupToLines(markup).map(parseContactLine).filter(Boolean);
  const blocks = [];
  let current = [];
  let currentHasLink = false;

  lines.forEach(line => {
    if(line.type === 'text' && currentHasLink && current.length > 0){
      blocks.push(current);
      current = [line];
      currentHasLink = false;
      return;
    }
    current.push(line);
    if(line.type === 'link') currentHasLink = true;
  });

  if(current.length > 0) blocks.push(current);
  return blocks;
}

function contactLinkProps(url) {
  const href = resolveProjectDocumentUrl(url);
  if(/^(mailto:|tel:)/i.test(href)) return {href};
  return {href, target: '_blank', rel: 'noopener noreferrer'};
}

const currentDomain = window.location.origin;


class PageProjectDetail extends React.Component{
  lastUrl = '';


  constructor(props){
    super(props);

    this.state = {
      cmsFeatureLines: [],
      cmsFeaturePoints: [],
      collisions: 0,
      currentSubTab: '',
      currentTab: '',
      featureDetailClosed: false,
      lastDesktopSubTab: '',
      legendShowInstalled: true,
      legendShowPlanned: true,
      height: window.innerHeight,
      introExpanded: false,
      introOverflows: false,
      mapOpen: false,
      mapConfigError: '',
      mapPluginReady: false,
      mobileSheetDetent: MOBILE_SHEET_DEFAULT_DETENT,
      mobileSheetDragPct: null,
      mobileSheetInsetPx: 0,
      mobileSheetView: 'list',
      photoLightboxIndex: 0,
      photoLightboxOpen: false,
      width: window.innerWidth,
    };
  }
  componentDidMount(){
    this.bindEvents();
    window.addEventListener('resize', this.updateDimensions);
    //console.log(`PageProjectDetail.componentDidMount()`, this.props);

    this.mapPluginReady = false;
    window.addEventListener('keydown', this.onDocumentKeyDown);
    this.measureIntroClamp();
    this.balancePhotoDots();
  }
  componentDidUpdate(prevProps, prevState){
    if(this.props.match.url != this.lastUrl) this.fetchContent();
    if(this.props.page?.id && this.props.page.id !== prevProps.page?.id){
      this.setState({
        mapConfigError: '',
        mapPluginReady: false,
        introExpanded: false,
        introOverflows: false,
        mapOpen: false,
        mobileSheetDetent: MOBILE_SHEET_DEFAULT_DETENT,
        mobileSheetDragPct: null,
        mobileSheetInsetPx: 0,
        mobileSheetView: 'list',
      }, this.measureIntroClamp);
      this.mapPluginReady = false;
      this._lastMobileChrome = null;
      this.photoWrapSnapper.cancel();
      this._scheduleMapReadyFallback();
      this.syncBodyScrollLock();
      return;
    }
    this.measureIntroClamp();
    this.balancePhotoDots();
    this.syncMobileMapLayoutIfNeeded(prevState);
  }
  componentWillUnmount(){
    window.removeEventListener('resize', this.updateDimensions);
    window.removeEventListener('keydown', this.onDocumentKeyDown);
    window.removeEventListener('message', this.recieveMessageFromChild);
    window.clearTimeout(this._mapReadyFallbackTimer);
    if(this._restoreMapScrollShield){
      this._restoreMapScrollShield();
      this._restoreMapScrollShield = null;
    }
    this.photoWrapSnapper.cancel();
    if(this._photoDotsFrame) window.cancelAnimationFrame(this._photoDotsFrame);
    document.body.classList.remove('page-project-details-map-expanded');
    document.body.style.overflow = '';
  }
  UNSAFE_componentWillMount(){
    this.fetchContent();
  }
  UNSAFE_componentWillReceiveProps(newProps){
    if(this.props.page.projectId !== newProps.page.projectId && newProps.page.projectId && newProps.page.projectId !== '' && this.mapPluginReady === true)
      this.featureOpt();
  }


  mapClose_onClick = () => {
    this.closeMobileMap();
  };
  onDocumentKeyDown = (event) => {
    if(event.key === 'Escape' && this.isMobileMapExpanded()){
      this.closeMobileMap();
    }
  };


  /**
   * This function binds events from this javascript file to the browser
   */
  bindEvents = () => {
    // Assign handler to message event from the browser window.
    if(window.addEventListener){
      window.addEventListener('message', this.recieveMessageFromChild, false);
    }
    else if(window.attachEvent){ // supports ie8
      window.attachEvent('onmessage', this.recieveMessageFromChild);
    }
  };
  canRenderDocuments = () => {
    return this.props.page.projectDocuments && this.props.page.projectDocuments.length > 0;
  };
  canRenderPartners = () => {
    return this.props.page.projectPartners && this.props.page.projectPartners.length > 0;
  };
  getProjectContacts = () => {
    const contacts = this.props.page.contacts;
    if(!Array.isArray(contacts)) return [];

    return contacts.filter(contact => (
      contact &&
      !contact.softDelete &&
      (contact.name || contact.description || contact.phoneNumber || contact.email || contact.address || contact.feedbackFormLink)
    ));
  };
  canRenderContact = () => {
    if(this.getProjectContacts().length > 0) return true;
    return this.props.page.contactDescription && this.props.page.contactDescription.length > 0;
  };
  getProjectPhotoItems = () => {
    return (this.props.page.photosAndVideos || []).filter(item => item && item.mediaImage && item.mediaImage.url);
  };
  canRenderPhotos = () => {
    return this.getProjectPhotoItems().length > 0;
  };
  hasBelowMapMainContent = () => {
    return this.canRenderPhotos() || this.canRenderDocuments() || this.canRenderPartners() || this.canRenderContact();
  };
  getBelowMapNews = () => {
    const news = this.props.page.latestNews;
    if(!Array.isArray(news) || news.length < 1) return [];
    return [...news]
      .filter(item => item && (item.newsDate || item.NewsDate))
      .sort((a, b) => {
        const aDate = new Date(a.newsDate || a.NewsDate).valueOf();
        const bDate = new Date(b.newsDate || b.NewsDate).valueOf();
        return bDate - aDate;
      })
      .slice(0, 4)
      .map(item => ({
        ...item,
        id: item.id || item.documentId,
        title: item.title || item.Title,
        slug: item.slug || item.Slug,
        newsDate: item.newsDate || item.NewsDate,
      }));
  };
  getBelowMapEvents = () => {
    const raw = this.props.page.latestEvents;
    if(!Array.isArray(raw) || raw.length < 1) return [];
    const events = raw.map(item => ({
      ...item,
      id: item.id || item.documentId,
      title: item.title || item.Title,
      slug: item.slug || item.Slug,
      eventDate: item.eventDate || item.EventDate,
      program: item.program || item.Program,
    })).filter(item => item.eventDate && !Number.isNaN(new Date(item.eventDate).getTime()));
    if(!events.length) return [];
    const now = Date.now();
    return events
      .filter(item => new Date(item.eventDate).getTime() >= now)
      .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
      .slice(0, 3);
  };
  getBelowMapViewAllHref = (path) => {
    // Project news is stored on RelatedProject, not Program. Passing ?program=
    // (often a stale _id) filters the News page to zero results (LLSU2-256).
    if(path === '/news') return '/news';
    const program = this.props.page.program;
    const programId = program && (program.documentId || program.id || program._id);
    if(programId) return `${path}?program=${programId}`;
    return path;
  };
  openPhotoLightbox = (index) => {
    this.setState({
      photoLightboxOpen: true,
      photoLightboxIndex: index,
    });
  };
  closePhotoLightbox = () => {
    this.setState({photoLightboxOpen: false});
  };
  getProjectPhotosAlign = () => (
    this.state.width >= helpers.screenSizes.lgMin ? 'center' : 'start'
  );
  photosCarouselRef = React.createRef();
  photosWrapRef = React.createRef();
  balancePhotoDots = () => {
    if(this._photoDotsFrame) window.cancelAnimationFrame(this._photoDotsFrame);
    this._photoDotsFrame = window.requestAnimationFrame(() => {
      this._photoDotsFrame = null;
      const dots = this.photosWrapRef.current && this.photosWrapRef.current.querySelector('.control-dots');
      applyBalancedDotRows(dots);
    });
  };
  photoWrapSnapper = createProjectPhotosWrapSnapper((itemListStyle) => (
    this.applyProjectPhotoWrapSnap(itemListStyle)
  ));
  applyProjectPhotoWrapSnap = (itemListStyle) => {
    const list = this.photosWrapRef.current && this.photosWrapRef.current.querySelector('.slider');
    if(list){
      [
        'WebkitTransform',
        'msTransform',
        'OTransform',
        'transform',
        'WebkitTransitionDuration',
        'MozTransitionDuration',
        'OTransitionDuration',
        'msTransitionDuration',
        'transitionDuration',
      ].forEach((prop) => {
        if(itemListStyle[prop] != null) list.style[prop] = itemListStyle[prop];
      });
      void list.offsetWidth;
    }
    if(this.photosCarouselRef.current){
      this.photosCarouselRef.current.setState({itemListStyle});
    }
  };
  projectPhotosAnimationHandler = (props, state) => (
    runProjectPhotosAnimationHandler(props, state, this.getProjectPhotosAlign(), this.photoWrapSnapper)
  );
  projectPhotosSwipeAnimationHandler = (delta, props, state, setState) => (
    runProjectPhotosSwipeAnimationHandler(
      delta,
      props,
      state,
      setState,
      this.getProjectPhotosAlign(),
      this.photoWrapSnapper,
    )
  );
  projectPhotosStopSwipingHandler = (props, state) => (
    runProjectPhotosStopSwipingHandler(props, state, this.getProjectPhotosAlign(), this.photoWrapSnapper)
  );
  /**
   * This function should recieve a JSON payload in the shape of features that will be rendered
   * on the page. This JSON object shape will need to be defined. At this moment we are using a
   * shallow state replacement on state.collisions
   *
   * Some samples are:
   *
   *  See Batch 2 LADOT-VisionZero-Maps-B-memorial
   *  memorial =  {
   *    livesLostWalking: 34,
   *    livesLostBiking: 12,
   *    livesLostDriving: 32,
   *  }
   *
   *  See Batch 1 Project-Details-B-Features
   *  feature = {
   *    pedestrianRefuge: 2,
   *    speedFeedbackSign: 4,
   *    crosswalkFlashBeacons: 2,
   *    leadignPedestrianIntevals: 14,
   *    protectedLeftTurn: 9,
   *    newTrafficSingal : 3
   *  }
   *

   *
   * @param {*} data
   */
  drawFeaturesByProject = data => {
    console.log(`drawFeaturesByProject()`, data);
    if(data.layer === 'cms_feature_points'){
      const {values} = data;
      this.setState({
        cmsFeaturePoints: values,
      }, this.syncDesktopFeaturePane);
    }
    else if(data.layer === 'cms_feature_lines'){
      const {values} = data;
      this.setState({
        cmsFeatureLines: values,
      }, this.syncDesktopFeaturePane);
    }
  };
  drawLayer = event => {
    //console.log("MapProgram -> drawLayer event: ", event);
    //console.log("MapProgram -> drawLayer event.target.value: ", event.target.value);
    //console.log("MapProgram -> drawLayer event.target.name: ", event.target.name);
    //console.log("MapProgram -> drawLayer event.target.getAttribute(map-layer): ", event.target.getAttribute("map-layer"));

    this.sendMessageToChild({
      functionName: event.target.getAttribute('service-task'),
      params: [event.target.getAttribute('map-layer'), event.target.value],
    });
  };
  statusFilterFromFlags = (showInstalled, showPlanned) => {
    if(showInstalled && showPlanned) return 'all';
    if(showInstalled) return 'Installed';
    if(showPlanned) return 'Planned';
    return 'none';
  };
  getFeatureStatusFilter = () => {
    return this.statusFilterFromFlags(this.state.legendShowInstalled, this.state.legendShowPlanned);
  };
  applyFeatureStatusFilter = (showInstalled, showPlanned) => {
    this.setState({
      legendShowInstalled: showInstalled,
      legendShowPlanned: showPlanned,
    });
    this.sendMessageToChild({
      functionName: 'filterFeaturesByStatus',
      params: [this.statusFilterFromFlags(showInstalled, showPlanned)],
    });
    if(this.state.currentSubTab){
      this.highlightFeatureType(this.state.currentSubTab);
    }
  };
  featureOpt = () => {
    if(!this.props.page.projectId || this.state.mapConfigError || !this.mapPluginReady) return;
    this.applyFeatureStatusFilter(true, true);
    this.sendMessageToChild({
      functionName: 'zoomToProject',
      params: [this.props.page.projectId],
    });
    this.sendMessageToChild({
      functionName: 'drawFeaturesByProject',
      params: [this.props.page.projectId, 'vision_zero'],
    });
  };
  clearFeatureStatusFilterOnMap = () => {
    if(this.state.legendShowInstalled && this.state.legendShowPlanned){
      return false;
    }
    this.applyFeatureStatusFilter(true, true);
    return true;
  };
  toggleLegendStatus = (status) => {
    const nextInstalled = status === 'Installed' ? !this.state.legendShowInstalled : this.state.legendShowInstalled;
    const nextPlanned = status === 'Planned' ? !this.state.legendShowPlanned : this.state.legendShowPlanned;
    this.applyFeatureStatusFilter(nextInstalled, nextPlanned);
  };
  fetchContent(){
    this.lastUrl = this.props.match.url;
    this.props.actions.initPageProjectDetail(this.props.match.params.slug.trim()).then(null, () => helpers.handle404(this.props.history));
  }
  handleMessageFromChild = data => {
    // DCR_Mapping test response
    // =============================

    //console.group("parent.html > parent.js > handleMessageFromChild", data);
    //console.log('data:', data);
    //console.log(typeof data);
    if(data && data.functionName){
      switch(data.functionName){
        case 'mapPluginReady':
          this.mapLoaded();
          break;
        case 'mapConfigError':
          window.clearTimeout(this._mapReadyFallbackTimer);
          this.mapPluginReady = false;
          this.setState({
            mapConfigError: data.message || 'Map configuration failed to load.',
            mapPluginReady: false,
          });
          break;
        case 'updateFeatureCount':
          this.updateFeatureCount(data);

          break;
        case 'selectedFeature':
          this.updateFeatureTab(data);
          break;
        case 'featureDeselected':
          if(this.shouldShowDesktopFeaturePane()){
            if(this.getFeatureStatusFilter() === 'all' && this.state.currentSubTab){
              this.selectFeatureType(this.state.currentSubTab);
            }
            break;
          }
          if(this.isMobileMapExpanded()){
            if(this.state.mobileSheetView === 'detail' || this.state.currentSubTab){
              this.showMobileSheetList();
            }
            break;
          }
          if(this.state.currentSubTab || this.state.currentTab === 'features'){
            this.setState({currentTab: '', currentSubTab: ''});
          }
          break;
        case 'drawFeaturesByProject':
          this.drawFeaturesByProject(data);
          break;
        case 'pageScroll':
          if(this.isMobileMapExpanded()) break;
          applyWheelScrollToWindow(window, data);
          break;
        case 'test':
          //console.log('testData from Child:', data);

          break;
        default:
        //console.warn("Error with payload data", data);
      }
    }
    //console.groupEnd();
  };
  isFeatureLegendVisible = () => {
    if(!this.state.mapPluginReady) return false;
    if(!this.hasRenderableMapFeatures()) return false;
    if(this.shouldUseMobileMapPreview()) return this.isMobileMapExpanded();
    return true;
  };
  isRenderableFeatureType = (value) => {
    return value != null && value !== '' && String(value).toLowerCase() !== 'null';
  };
  isDesktopMapLayout = () => {
    const width = this.state.width || window.innerWidth;
    return width >= helpers.screenSizes.lgMin;
  };
  getRenderableMapFeatures = () => {
    const fromPoints = (this.state.cmsFeaturePoints || []).filter(item => this.isRenderableFeatureType(item.value));
    const fromLines = (this.state.cmsFeatureLines || []).filter(item => this.isRenderableFeatureType(item.value));
    return fromPoints.concat(fromLines);
  };
  hasRenderableMapFeatures = () => this.getRenderableMapFeatures().length > 0;
  shouldShowDesktopFeaturePane = () => this.isDesktopMapLayout() && this.hasRenderableMapFeatures();
  shouldUseMobileMapPreview = () => !this.isDesktopMapLayout();
  isMobileMapExpanded = () => this.shouldUseMobileMapPreview() && this.state.mapOpen;
  shouldShowMobileBottomSheet = () => this.isMobileMapExpanded() && this.hasRenderableMapFeatures();
  getDisplayedSheetPct = () => {
    if(this.state.mobileSheetDragPct != null) return this.state.mobileSheetDragPct;
    return this.state.mobileSheetDetent || MOBILE_SHEET_DEFAULT_DETENT;
  };
  getMobileSheetHeightPx = () => {
    let vh = this.state.height || window.innerHeight || 0;
    if(this.isMobileMapExpanded()){
      const overlay = document.querySelector('.page-project-details__map-iframe-container');
      const overlayH = overlay && overlay.getBoundingClientRect().height;
      if(overlayH) vh = overlayH;
    }
    return Math.round((this.getDisplayedSheetPct() / 100) * vh);
  };
  getMobileMapInsetPx = () => {
    if(!this.shouldShowMobileBottomSheet()) return 0;
    if(this.state.mobileSheetDragPct != null){
      return this.state.mobileSheetInsetPx || 0;
    }
    return this.getMobileSheetHeightPx();
  };
  resetMobileSheetState = () => {
    const vh = this.state.height || window.innerHeight || 0;
    const detent = MOBILE_SHEET_DEFAULT_DETENT;
    return {
      mobileSheetDetent: detent,
      mobileSheetDragPct: null,
      mobileSheetInsetPx: Math.round((detent / 100) * vh),
      mobileSheetView: 'list',
    };
  };
  expandMobileMap = () => {
    this.setState({
      mapOpen: true,
      ...this.resetMobileSheetState(),
    });
  };
  closeMobileMap = () => {
    if(!this.shouldUseMobileMapPreview()){
      this.setState({mapOpen: false});
      return;
    }
    this.setState({
      mapOpen: false,
      currentSubTab: '',
      ...this.resetMobileSheetState(),
    });
  };
  showMobileSheetList = () => {
    this.setState({
      mobileSheetView: 'list',
      currentSubTab: '',
      mobileSheetInsetPx: this.getMobileSheetHeightPx(),
    });
    this.sendMessageToChild({
      functionName: 'clearSelectedFeature',
      params: [false],
    });
  };
  syncBodyScrollLock = () => {
    const lock = this.state.mapOpen && !this.isDesktopMapLayout();
    document.body.classList.toggle('page-project-details-map-expanded', lock);
    document.body.style.overflow = lock ? 'hidden' : '';
  };
  syncMobileMapChrome = () => {
    if(this.isDesktopMapLayout()){
      if(this._lastMobileChrome === 'desktop') return;
      this.sendMessageToChild({
        functionName: 'syncMobileMapLayout',
        params: [0, 0, true],
      });
      this._lastMobileChrome = 'desktop';
      return;
    }
    if(!this.state.mapOpen){
      if(this._lastMobileChrome === 'preview') return;
      this.sendMessageToChild({
        functionName: 'syncMobileMapLayout',
        params: [0, 0, false],
      });
      this._lastMobileChrome = 'preview';
      return;
    }
    const dragging = this.state.mobileSheetDragPct != null;
    const sheetPx = this.shouldShowMobileBottomSheet() ? this.getMobileSheetHeightPx() : 0;
    const insetPx = this.getMobileMapInsetPx();
    // Pass the live sheet height so Esri widgets can stay a fixed gap above it
    // even while map padding animates to a new detent.
    const chromeOffset = sheetPx;
    const keepVisibleCenter = !dragging && insetPx > 0;
    const key = `open|${chromeOffset}|${insetPx}|${keepVisibleCenter}`;
    if(this._lastMobileChrome === key) return;
    this._lastMobileChrome = key;
    this.sendMessageToChild({
      functionName: 'syncMobileMapLayout',
      params: [chromeOffset, insetPx, true, keepVisibleCenter],
    });
  };
  syncMobileMapLayoutIfNeeded = (prevState) => {
    if(!prevState) return;
    const changed = (
      prevState.mapOpen !== this.state.mapOpen ||
      prevState.mobileSheetDetent !== this.state.mobileSheetDetent ||
      prevState.mobileSheetDragPct !== this.state.mobileSheetDragPct ||
      prevState.mobileSheetView !== this.state.mobileSheetView ||
      prevState.mobileSheetInsetPx !== this.state.mobileSheetInsetPx ||
      prevState.height !== this.state.height ||
      prevState.width !== this.state.width
    );
    if(changed){
      this.syncMobileMapChrome();
      this.syncBodyScrollLock();
      if(!prevState.mapOpen && this.state.mapOpen){
        requestAnimationFrame(() => {
          this._lastMobileChrome = null;
          this.syncMobileMapChrome();
        });
      }
    }
  };
  onSheetPointerDown = (event) => {
    if(event.button != null && event.button !== 0) return;
    const clientY = event.clientY;
    if(!Number.isFinite(clientY)) return;
    event.preventDefault();
    this._sheetDrag = {
      startY: clientY,
      startPct: this.getDisplayedSheetPct(),
      currentPct: this.getDisplayedSheetPct(),
    };
    if(event.currentTarget.setPointerCapture && event.pointerId != null){
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    this.setState({mobileSheetDragPct: this._sheetDrag.currentPct});
  };
  onSheetPointerMove = (event) => {
    if(!this._sheetDrag) return;
    const clientY = event.clientY;
    if(!Number.isFinite(clientY)) return;
    const vh = this.state.height || window.innerHeight || 1;
    const deltaPct = ((this._sheetDrag.startY - clientY) / vh) * 100;
    const next = applySheetResistance(this._sheetDrag.startPct + deltaPct);
    this._sheetDrag.currentPct = next;
    this.setState({mobileSheetDragPct: next});
  };
  onSheetPointerUp = () => {
    if(!this._sheetDrag) return;
    const pct = this._sheetDrag.currentPct;
    this._sheetDrag = null;
    const snapped = snapSheetDetent(pct);
    const vh = this.state.height || window.innerHeight || 0;
    this.setState({
      mobileSheetDetent: snapped,
      mobileSheetDragPct: null,
      mobileSheetInsetPx: Math.round((snapped / 100) * vh),
    });
  };
  getMapIframeSrc = () => {
    return `/maps/child.html?hidePopup=true&disableScrollZoom=true&cms_path=${apiValues.getCmsPath()}&centroid_level=300000`;
  };
  highlightFeatureType = (featureType) => {
    this.sendMessageToChild({
      functionName: 'highlightFeaturesByType',
      params: ['cms_feature_points', featureType],
    });
    this.sendMessageToChild({
      functionName: 'highlightFeaturesByType',
      params: ['cms_feature_lines', featureType],
    });
  };
  selectFeatureType = (featureType, options = {}) => {
    if(!this.isRenderableFeatureType(featureType)) return;
    this.clearFeatureStatusFilterOnMap();
    const next = {currentTab: 'features', currentSubTab: featureType, featureDetailClosed: false};
    if(this.isDesktopMapLayout()){
      next.lastDesktopSubTab = featureType;
    }
    if(options.mobileSheetView){
      next.mobileSheetView = options.mobileSheetView;
      if(options.mobileSheetView === 'detail'){
        next.mobileSheetInsetPx = this.getMobileSheetHeightPx();
      }
    }
    this.setState(next);
    this.highlightFeatureType(featureType);
  };
  closeDesktopFeatureDetail = () => {
    this.setState({
      currentSubTab: '',
      featureDetailClosed: true,
    });
    this.highlightFeatureType('reset');
  };
  toggleDesktopFeatureType = (featureType) => {
    if(this.state.currentSubTab === featureType){
      this.closeDesktopFeatureDetail();
      return;
    }
    this.selectFeatureType(featureType);
  };
  syncDesktopFeaturePane = () => {
    if(!this.isDesktopMapLayout()) return;
    const features = this.getRenderableMapFeatures();
    if(!features.length){
      if(this.state.currentTab === 'features' || this.state.currentSubTab){
        this.setState({currentTab: '', currentSubTab: ''});
      }
      return;
    }
    const next = {};
    if(this.state.currentTab !== 'features'){
      next.currentTab = 'features';
    }
    const current = this.state.currentSubTab;
    if(current && !features.some(item => item.value === current)){
      next.currentSubTab = '';
    }
    if(Object.keys(next).length){
      this.setState(next);
    }
  };
  getProjectFeatureForSubTab = (featureType) => {
    if(!featureType) return null;
    return resolveProjectFeatureForType(
      this.props.page.projectFeatures,
      this.props.page.featureCatalog,
      featureType,
    );
  };
  renderProjectFeatureBody = (projectFeature) => {
    const imgStyles = {
      backgroundImage: `url(${apiValues.imgPath(projectFeature.image && projectFeature.image.url)})`,
    };
    const locations = (projectFeature.projectFeatureLocations || []).map((projectFeatureLocation) => (
      <div key={projectFeatureLocation.id} className="page-project-details__feature-location__item">
        {projectFeatureLocation.title}
      </div>
    ));

    return (
      <>
        {projectFeature.image &&
         <div className="page-project-details__feature-sub-tab-img" style={imgStyles}>
           <div className="d-3-2"></div>
         </div>}
        <MarkdownField
          className="page-project-details__feature-description"
          markup={wrapMarkdownIframes(projectFeature.description)}
        />
        {locations.length > 0 ? (
          <div className="page-project-details__feature-location">
            <div className="page-project-details__feature-location__title">LOCATIONS</div>
            {locations}
          </div>
        ) : null}
      </>
    );
  };
  _scheduleMapReadyFallback = () => {
    window.clearTimeout(this._mapReadyFallbackTimer);
    this._mapReadyFallbackTimer = window.setTimeout(() => {
      if(!this.state.mapPluginReady && !this.state.mapConfigError){
        this.setState({ mapPluginReady: true });
      }
    }, 12000);
  };

  mapLoaded = () => {
    window.clearTimeout(this._mapReadyFallbackTimer);
    this.mapPluginReady = true;
    this.featureOpt();
    this.setState({ mapPluginReady: true, mapConfigError: '' }, this.syncMobileMapChrome);
  };
  /**
   * This function listens for an event from the browser message event and returns a JSON
   * @param {*} event
   */
  recieveMessageFromChild = event => {
    //console.group("MapProgram parent.html > parent.js > recieveMessageFromChild");
    //console.log(event);

    if(event.origin === currentDomain){ //WARNING this is used to verify the source of the message, aka whitelist
      // Retrieve data sent in postMessage

      //console.log("MapProgram event.origin", event.origin);
      //console.log("MapProgram currentDomain", currentDomain);
      //console.log("MapProgram event.data", event.data);
      if(event.data){
        //console.log("Message received from child:", JSON.parse(event.data));
        //console.groupEnd();
        let data;
        if(typeof event.data === 'string'){
          data = JSON.parse(event.data);
        }
        else{
          data = event.data;
        }
        this.handleMessageFromChild(data);

        return data;
      }
      else{
        //console.warn("Check message payload on event.data", event.data);
      }
    }
    else{
      //console.warn("Error with validating origin: " + event.origin + " !== " + currentDomain);
    }
  };
  /**
   * This function sents a message from the parent to the child.
   *
   * It can be called by passing a JSON as an example
   *
   * sentMessageToChild({ //payload is being defined at the moment w/ DCR Maps Team.
        mapMode: programs-projects, // can also be program-projects, project-details, feedback, memorial
        program: [vision_zero, active_transporation, ...]
        projects: [data_collection_feedback, plannding_design, construction, ...] , // can also be data_collection_feedback, planning_design, construction, completed, post_project_evaluation, active
        ui_ZoomLevel: "lab-zoom", // defines HTML element showing current zoomlevel,
   * })
   *
   */
  sendMessageToChild = payload => {
    //console.group("parent.html > parent.js > sendMessageToChild");
    //console.log(payload);
    if(this.state.mapConfigError || !this.mapPluginReady) return;
    if(payload && this.iframe){
      this.iframe.contentWindow.postMessage(JSON.stringify(payload), currentDomain);
    }

    //console.log("MapProgram -> dcrMappingData: ", dcrMappingData);
    //console.groupEnd();
  };
  updateDimensions = () => {
    this.setState({
      height: window.innerHeight,
      width: window.innerWidth,
    }, () => {
      this.measureIntroClamp();
      this.balancePhotoDots();
      this.syncDesktopFeaturePane();
      if(this.isDesktopMapLayout() && this.state.mapOpen){
        this.closeMobileMap();
      }
      else{
        this.syncBodyScrollLock();
        this.syncMobileMapChrome();
      }
    });
  };
  measureIntroClamp = () => {
    const el = this.introCopyEl;
    if(!el){
      if(this.state.introOverflows) this.setState({introOverflows: false});
      return;
    }
    if(this.state.introExpanded){
      el.style.maxHeight = '';
      return;
    }

    const lineBottoms = collectIntroLineBottoms(el);
    const snappedHeight = introClampMaxHeightPx(lineBottoms);
    if(snappedHeight != null){
      el.style.maxHeight = `${snappedHeight}px`;
    }
    else{
      el.style.maxHeight = '';
    }

    const overflowing = snappedHeight != null || el.scrollHeight > el.clientHeight + 1;
    if(overflowing !== this.state.introOverflows){
      this.setState({introOverflows: overflowing});
    }
  };
  toggleIntroExpanded = () => {
    this.setState((state) => ({introExpanded: !state.introExpanded}));
  };
  updateFeatureCount = data => {
    //console.log(`updateFeatureCount()`, data);
    this.setState({collisions: data.count});
  };
  updateFeatureTab = data => {
    console.log(`updateFeatureTab()`, data);
    const {values} = data;

    switch(data.layer){
      case  'cms_feature_points':
      case 'cms_feature_lines':
        this.setState(
          {
            currentTab: 'features',
            currentSubTab: values.feature_type,
            featureDetailClosed: false,
            lastDesktopSubTab: this.isDesktopMapLayout() ? values.feature_type : this.state.lastDesktopSubTab,
            mobileSheetView: this.isMobileMapExpanded() ? 'detail' : this.state.mobileSheetView,
            mobileSheetInsetPx: this.isMobileMapExpanded() ? this.getMobileSheetHeightPx() : this.state.mobileSheetInsetPx,
          },
        );
        this.sendMessageToChild({
          functionName: 'highlightFeaturesByType',
          params: ['cms_feature_points', values.feature_type],
        });
        this.sendMessageToChild({
          functionName: 'highlightFeaturesByType',
          params: ['cms_feature_lines', values.feature_type],
        });
        break;
    }

    // drawLayerFeature("cms_feature_lines", "reset");
  };


  renderBreadcrumbs = () => {
    let mBreadcrumbs = {
      pageChain: [
        {browserTitle: 'Home', slug: ''},
        {browserTitle: 'Our Projects', slug: 'projects'},
        {browserTitle: this.props.page.title, slug: '#'},
      ],
    };
    //console.log(mBreadcrumbs);

    return (
      <MBreadcrumbs className="coupling coupling--full-centered m-breadcrumbs--project-details"
                    mBreadcrumbs={mBreadcrumbs}/>
    );
  };
  renderCouncilDistrictNames = () => {
    if(!this.props.page.councilDistrictNames) return null;

    const pluralStr = this.props.page.councilDistrictNames.split('](').length > 1 ? 's' : '';

    return (
      <div className="page-project-details__line-info">
        <span className="page-project-details__info-label">Council District{pluralStr}: </span>
        <MarkdownField className="d-inline-block page-project-details__info-description" markup={this.props.page.councilDistrictNames}/>
      </div>
    );
  };
  renderProjectStatus = () => {
    const phaseLabel = resolveProjectPhaseLabel(this.props.page);
    if(!phaseLabel) return null;

    return (
      <div className="page-project-details__line-info">
        <span className="page-project-details__info-label">Current Phase: </span>
        <span className="page-project-details__info-description">{phaseLabel}</span>
      </div>
    );
  };
  renderDescription = () => {
    if(!this.props.page.description || this.props.page.description.length < 1) return null;

    return (<MarkdownField className="description" markup={this.props.page.description}/>);
  };
  renderDocuments = () => {
    if(!this.canRenderDocuments()) return null;

    const documents = parseProjectDocumentLinks(this.props.page.projectDocuments);

    return (
      <section className="page-project-details__below-section page-project-details__below-section--documents" aria-labelledby="project-documents-heading">
        <h2 id="project-documents-heading" className="page-project-details__below-title">PROJECT DOCUMENTS</h2>
        {documents.length > 0 ? (
          <ul className="page-project-details__documents">
            {documents.map((document, index) => (
              <li key={`${document.url}-${index}`}>
                <a href={resolveProjectDocumentUrl(document.url)}
                   target="_blank"
                   rel="noopener noreferrer">
                  {document.title}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <MarkdownField className="page-project-details__documents" markup={this.props.page.projectDocuments}/>
        )}
      </section>
    );
  };
  renderBannerImage = () => {
    const thumbnail = this.props.page?.thumbnail;
    if(!thumbnail?.url) return null;

    return (
      <div className="page-project-details__hero-media page-project-details__banner">
        <img
          alt=""
          className="page-project-details__hero-photo page-project-details__banner-img"
          src={apiValues.imgPath(thumbnail.url)}
        />
      </div>
    );
  };
  renderIntroSection = () => {
    return (
      <div className="page-project-details__intro coupling coupling--centered">
        <div className="container container--coupling">
          <div className="page-project-details__hero">
            <div className="page-project-details__hero-copy">
              <div className="page-project-details__intro-title-container">
                <div>
                  <ProgramBanner program={this.props.page.program}/>
                </div>
                <h1 className="page-project-details__intro-title">
                  {this.props.page.title}
                </h1>
              </div>
              {this.renderTags('page-project-details__hero-tags', true)}
              <div className="page-project-details__hero-meta">
                {this.renderLocationDescription()}
                {this.renderProjectStatus()}
                {this.renderNeighborhoodCouncilName()}
                {this.renderCouncilDistrictNames()}
              </div>
              {this.renderIntroDescription()}
            </div>
            {this.renderBannerImage()}
          </div>
        </div>
      </div>
    );
  };
  renderIntroDescription = () => {
    const markup = mergeProjectIntroMarkup(this.props.page);
    if(!markup) return null;

    const copyClass = this.state.introExpanded
      ? 'page-project-details__intro-copy'
      : 'page-project-details__intro-copy is-clamped';

    return (
      <div className="page-project-details__intro-body">
        <div
          className={copyClass}
          ref={(el) => {
            this.introCopyEl = el;
          }}
        >
          <MarkdownField className="page-project-details__intro-description" markup={markup}/>
        </div>
        {this.state.introOverflows ? (
          <button
            type="button"
            className="page-project-details__intro-toggle"
            aria-expanded={this.state.introExpanded}
            onClick={this.toggleIntroExpanded}
          >
            {this.state.introExpanded ? 'Read less' : 'Read more'}
            <span className="page-project-details__intro-toggle-chevron" aria-hidden="true">
              <svg viewBox="0 0 12 8" width="12" height="8" focusable="false">
                <path
                  d="M1.5 1.75 L6 6.25 L10.5 1.75"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        ) : null}
      </div>
    );
  };
  renderLegend = () => {
    if(!this.isFeatureLegendVisible()) return null;

    const renderRow = (status, pinClass, checked) => (
      <label key={status} className="page-project-details__legend-item">
        <input
          type="checkbox"
          className="page-project-details__legend-check"
          checked={checked}
          onChange={() => this.toggleLegendStatus(status)}
        />
        <span className={`page-project-details__legend-pin ${pinClass}`} aria-hidden="true"/>
        <span className="page-project-details__legend-title">{status}</span>
      </label>
    );

    const sheetPct = this.shouldShowMobileBottomSheet() ? this.getDisplayedSheetPct() : null;
    const style = sheetPct != null ? {bottom: getMobileSheetLegendBottomStyle(sheetPct)} : undefined;

    return (
      <div className="page-project-details__legend" role="group" aria-label="Map legend" style={style}>
        {renderRow('Installed', 'page-project-details__legend-pin--installed', this.state.legendShowInstalled)}
        {renderRow('Planned', 'page-project-details__legend-pin--planned', this.state.legendShowPlanned)}
      </div>
    );
  };
  renderLocationDescription = () => {
    if(!this.props.page.locationDescription || this.props.page.locationDescription.length < 1) return null;

    return (
      <div className="page-project-details__line-info">
        <span className="page-project-details__info-label">Location: </span>
        <span className="page-project-details__info-description">{this.props.page.locationDescription}</span>
      </div>
    );
  };
  renderMapLoadingIconDesktop = () => {
    if(this.state.mapPluginReady || this.state.mapConfigError) return null;

    return (<img alt="Loading" src={loadingIcon} className="map-loading-icon"/>);
  };
  renderMapLoadingIconMobile = () => {
    if(this.state.mapPluginReady || this.state.mapConfigError) return null;
    if(this.isDesktopMapLayout()) return null;

    return (<img alt="Loading" src={loadingIcon} className="map-loading-icon-mobile"/>);
  };
  renderDesktopFeaturePane = () => {
    const features = this.getRenderableMapFeatures();
    if(!features.length) return null;

    const selected = this.state.currentSubTab;
    const isDetailOpen = !!selected;
    const detailType = selected || this.state.lastDesktopSubTab;
    const projectFeature = this.getProjectFeatureForSubTab(detailType);

    return (
      <aside
        className={`page-project-details__feature-pane${isDetailOpen ? ' page-project-details__feature-pane--detail' : ''}`}
        aria-label="Project features"
      >
        <div className="page-project-details__feature-list">
          <h2 className="page-project-details__feature-list-title">PROJECT FEATURES</h2>
          <ul className="page-project-details__feature-list-items">
            {features.map((feature) => {
              const isSelected = selected === feature.value;
              return (
                <li key={feature.value}>
                  <button
                    type="button"
                    className={`page-project-details__feature-list-item${isSelected ? ' is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => this.toggleDesktopFeatureType(feature.value)}
                  >
                    <span className="page-project-details__feature-list-name">{feature.value}</span>
                    <span className="page-project-details__feature-list-count">{feature.count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div
          className="page-project-details__feature-detail-slot"
          aria-hidden={!isDetailOpen}
          inert={isDetailOpen ? undefined : ''}
        >
          <div className="page-project-details__feature-detail">
            {detailType ? (
              <div className="page-project-details__feature-detail-header">
                <h3 className="page-project-details__feature-detail-title">{detailType}</h3>
                <button
                  type="button"
                  className="page-project-details__feature-detail-close"
                  aria-label="Close feature detail"
                  tabIndex={isDetailOpen ? 0 : -1}
                  onClick={this.closeDesktopFeatureDetail}
                />
              </div>
            ) : null}
            {projectFeature ? (
              <div className="page-project-details__feature-sub-tab">
                {this.renderProjectFeatureBody(projectFeature)}
              </div>
            ) : null}
          </div>
        </div>
      </aside>
    );
  };
  renderMapSidebar = () => {
    if(!this.isDesktopMapLayout()){
      return null;
    }
    return this.renderDesktopFeaturePane();
  };
  renderMobileMapClose = () => {
    if(!this.state.mapOpen) return null;

    return (
      <button className="page-project-details__map-button page-project-details__map-button--close"
              aria-label="Close map"
              onClick={this.mapClose_onClick}>Close</button>
    );
  };
  renderMapScrollShield = () => {
    if(this.shouldUseMobileMapPreview() || this.state.mapConfigError) return null;

    return (
      <div
        className="page-project-details__map-scroll-shield"
        aria-hidden="true"
        onPointerDown={this.onMapScrollShieldPointerDown}
      />
    );
  };
  onMapScrollShieldPointerDown = (event) => {
    if(this._restoreMapScrollShield){
      this._restoreMapScrollShield();
    }
    this._restoreMapScrollShield = passPointerThroughMapShield(
      event.currentTarget,
      this.iframe,
      event,
    );
  };
  renderMobileMapHitLayer = () => {
    if(!this.shouldUseMobileMapPreview() || this.state.mapOpen) return null;

    return (
      <button
        type="button"
        className="page-project-details__map-hit-layer"
        aria-label="Expand map"
        onClick={this.expandMobileMap}
      >
        <span className="page-project-details__map-expand" aria-hidden="true">
          <img className="page-project-details__map-expand-icon" src={iconExpandMap} alt=""/>
        </span>
      </button>
    );
  };
  renderMobileSheetList = (features) => {
    return (
      <ul className="page-project-details__map-sheet-list">
        {features.map((feature) => (
          <li key={feature.value}>
            <button
              type="button"
              className="page-project-details__map-sheet-item"
              onClick={() => this.selectFeatureType(feature.value, {mobileSheetView: 'detail'})}
            >
              <span className="page-project-details__map-sheet-item-name">{feature.value}</span>
              <span className="page-project-details__map-sheet-item-count">{feature.count}</span>
              <img alt="" className="page-project-details__map-sheet-item-chevron" src={iconCaretRight}/>
            </button>
          </li>
        ))}
      </ul>
    );
  };
  renderMobileSheetDetail = (projectFeature) => {
    const title = this.state.currentSubTab;
    return (
      <div className="page-project-details__map-sheet-detail">
        <button
          type="button"
          className="page-project-details__map-sheet-back"
          aria-label="Back to feature list"
          onClick={this.showMobileSheetList}
        >
          <img alt="" src={iconBack}/>
        </button>
        <h2 className="page-project-details__map-sheet-title">{title}</h2>
        {projectFeature ? this.renderProjectFeatureBody(projectFeature) : null}
      </div>
    );
  };
  renderMobileBottomSheet = () => {
    if(!this.shouldShowMobileBottomSheet()) return null;

    const pct = this.getDisplayedSheetPct();
    const isDetail = this.state.mobileSheetView === 'detail' && !!this.state.currentSubTab;
    const projectFeature = isDetail ? this.getProjectFeatureForSubTab(this.state.currentSubTab) : null;
    const features = this.getRenderableMapFeatures();
    const label = isDetail ? this.state.currentSubTab : 'Map features';

    return (
      <div
        className={`page-project-details__map-sheet${this.state.mobileSheetDragPct != null ? ' is-dragging' : ''}`}
        style={{height: getMobileSheetHeightStyle(pct)}}
        role="dialog"
        aria-label={label}
      >
        <div
          className="page-project-details__map-sheet-handle"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize feature sheet"
          onPointerDown={this.onSheetPointerDown}
          onPointerMove={this.onSheetPointerMove}
          onPointerUp={this.onSheetPointerUp}
          onPointerCancel={this.onSheetPointerUp}
        >
          <span className="page-project-details__map-sheet-handle-bar"/>
        </div>
        <div className="page-project-details__map-sheet-body">
          {isDetail ? this.renderMobileSheetDetail(projectFeature) : this.renderMobileSheetList(features)}
        </div>
      </div>
    );
  };
  renderMapSection = () => {
    const displayClass = this.state.mapOpen ? 'page-project-details__map--is-open' : '';
    const mapClassNames = ['page-project-details__map', displayClass];
    if(this.shouldUseMobileMapPreview() && !this.state.mapOpen){
      mapClassNames.push('page-project-details__map--preview');
    }
    if(this.shouldShowDesktopFeaturePane()){
      mapClassNames.push('page-project-details__map--has-features');
    }
    else if(this.isDesktopMapLayout()){
      mapClassNames.push('page-project-details__map--full');
    }
    const mapClass = mapClassNames.filter(Boolean).join(' ');

    const mapInner = this.state.mapConfigError ? (
      <div className={mapClass}>
        {this.renderMapSidebar()}
        <div className="page-project-details__map-iframe-container">
          <p className="coupling coupling--centered-thin page-project-details__map-error" role="alert">
            {this.state.mapConfigError}
            {' '}
            Check that the CMS is running and <code>/api/map-config</code> is available at {apiValues.getCmsPath()}.
          </p>
        </div>
      </div>
    ) : (
      <div className={mapClass}>
        {this.renderMapSidebar()}
        <div className="page-project-details__map-iframe-container">
          {this.renderMobileMapClose()}
          {this.renderMapLoadingIconDesktop()}
          {this.renderMapLoadingIconMobile()}
          <div className="page-project-details__map-iframe-wrap">
            <iframe className="page-project-details__map-iframe"
                    title={MAP_IFRAME_TITLE}
                    src={this.getMapIframeSrc()}
                    ref={(event) => this.iframe = event}/>
            {this.renderMapScrollShield()}
            {this.renderMobileMapHitLayer()}
          </div>
          {this.renderLegend()}
          {this.renderMobileBottomSheet()}
        </div>
      </div>
    );

    return (
      <div className="page-project-details__map-section coupling coupling--centered">
        <div className="container container--coupling">
          {mapInner}
        </div>
      </div>
    );
  };
  renderNeighborhoodCouncilName = () => {
    if(!this.props.page.neighborhoodCouncilName) return null;

    return (
      <div className="page-project-details__line-info">
        <span className="page-project-details__info-label">Neighborhood Council: </span>
        <span className="page-project-details__info-description">{this.props.page.neighborhoodCouncilName}</span>
      </div>
    );
  };
  renderPartners = () => {
    if(!this.canRenderPartners()) return null;

    return (
      <section className="page-project-details__below-section page-project-details__below-section--partners" aria-labelledby="project-partners-heading">
        <h2 id="project-partners-heading" className="page-project-details__below-title">PARTNERS</h2>
        <ul className="page-project-details__partners">
          {this.props.page.projectPartners.map(({title, linkUrl, id}) => (
            <li key={id}>
              {linkUrl ? (
                <a {...contactLinkProps(linkUrl)}>{title}</a>
              ) : (
                <span>{title}</span>
              )}
            </li>
          ))}
        </ul>
      </section>
    );
  };
  renderContactLines = (text, keyPrefix) => {
    return String(text).split('\n').map((line, index) => (
      <p key={`${keyPrefix}-${index}`}>{line}</p>
    ));
  };
  renderStructuredContacts = (contacts) => {
    return (
      <div className="page-project-details__contact">
        {contacts.map(contact => (
          <div className="page-project-details__contact-block" key={contact.id || contact.documentId}>
            {contact.name ? <p>{contact.name}</p> : null}
            {contact.description ? this.renderContactLines(contact.description, 'description') : null}
            {contact.phoneNumber ? <p>{contact.phoneNumber}</p> : null}
            {contact.email ? (
              <a {...contactLinkProps(`mailto:${contact.email}`)}>{contact.email}</a>
            ) : null}
            {contact.address ? this.renderContactLines(contact.address, 'address') : null}
            {contact.feedbackFormLink ? (
              <p>
                Or use our <a {...contactLinkProps(contact.feedbackFormLink)}>Feedback Form</a>
              </p>
            ) : null}
          </div>
        ))}
      </div>
    );
  };
  renderContactInformation = () => {
    if(!this.canRenderContact()) return null;

    const contacts = this.getProjectContacts();
    const blocks = contacts.length > 0 ? [] : parseProjectContactBlocks(this.props.page.contactDescription);

    return (
      <section className="page-project-details__below-section page-project-details__below-section--contact" aria-labelledby="project-contact-heading">
        <h2 id="project-contact-heading" className="page-project-details__below-title">CONTACT INFORMATION</h2>
        {contacts.length > 0 ? this.renderStructuredContacts(contacts) : null}
        {contacts.length < 1 && blocks.length > 0 ? (
          <div className="page-project-details__contact">
            {blocks.map((block, blockIndex) => (
              <div className="page-project-details__contact-block" key={blockIndex}>
                {block.map((line, lineIndex) => (
                  line.type === 'link' ? (
                    <a key={lineIndex} {...contactLinkProps(line.url)}>
                      {line.title}
                    </a>
                  ) : (
                    <p key={lineIndex}>{line.value}</p>
                  )
                ))}
              </div>
            ))}
          </div>
        ) : null}
        {contacts.length < 1 && blocks.length < 1 ? (
          <MarkdownField className="page-project-details__contact" markup={this.props.page.contactDescription}/>
        ) : null}
      </section>
    );
  };
  renderProjectPhotos = () => {
    const items = this.getProjectPhotoItems();
    if(items.length < 1) return null;

    const isDesktop = this.state.width >= helpers.screenSizes.lgMin;
    const showControls = items.length > 1;

    return (
      <section className="page-project-details__below-section" aria-labelledby="project-photos-heading">
        <h2 id="project-photos-heading" className="page-project-details__below-title">PROJECT PHOTOS</h2>
        <div
          className={`page-project-details__photos page-project-details__photos--${this.getProjectPhotosAlign()}`}
          ref={this.photosWrapRef}
          style={{'--photo-slide-pct': `${isDesktop ? PROJECT_PHOTOS_DESKTOP_SLIDE_PCT : PROJECT_PHOTOS_MOBILE_SLIDE_PCT}%`}}
        >
          <Carousel
            key={isDesktop ? 'photos-lg' : 'photos-sm'}
            animationHandler={this.projectPhotosAnimationHandler}
            centerMode={showControls}
            centerSlidePercentage={isDesktop ? PROJECT_PHOTOS_DESKTOP_SLIDE_PCT : PROJECT_PHOTOS_MOBILE_SLIDE_PCT}
            emulateTouch={showControls}
            infiniteLoop={showControls}
            onClickItem={this.openPhotoLightbox}
            onSwipeStart={this.photoWrapSnapper.flush}
            ref={this.photosCarouselRef}
            renderIndicator={(onClickHandler, isSelected, index) => (
              <li key={index} className={isSelected ? 'dot selected' : 'dot'} role="presentation">
                <button
                  type="button"
                  className="page-project-details__photos-dot"
                  onClick={onClickHandler}
                  aria-label={`Go to photo ${index + 1}`}
                  aria-current={isSelected ? 'true' : undefined}
                />
              </li>
            )}
            showArrows={showControls && isDesktop}
            showIndicators={showControls}
            showStatus={false}
            showThumbs={false}
            stopSwipingHandler={this.projectPhotosStopSwipingHandler}
            swipeAnimationHandler={this.projectPhotosSwipeAnimationHandler}
          >
            {items.map((item) => (
              <div className="page-project-details__photo" key={item.id || item.mediaImage.url}>
                <img alt={item.alt || item.caption || ''} src={apiValues.imgPath(item.mediaImage.url)}/>
              </div>
            ))}
          </Carousel>
        </div>
        <Lightbox
          isOpen={this.state.photoLightboxOpen}
          mediaItems={items}
          onCloseClick={this.closePhotoLightbox}
          selectedItem={this.state.photoLightboxIndex}
        />
      </section>
    );
  };
  renderProjectUpdates = () => {
    const news = this.getBelowMapNews();
    if(!news.length) return null;

    return (
      <section className="page-project-details__below-section page-project-details__updates" aria-labelledby="project-updates-heading">
        <div className="page-project-details__below-header">
          <h2 id="project-updates-heading" className="page-project-details__below-title">PROJECT UPDATES</h2>
          <SuperLink to={this.getBelowMapViewAllHref('/news')} className="page-project-details__below-view-all">View all</SuperLink>
        </div>
        <div className="page-project-details__updates-list">
          {news.map(item => (
            <NewsItem
              key={item.id || item.slug}
              className="page-project-details__update-item"
              disableDescription
              disableLocation
              event={item}
            />
          ))}
        </div>
      </section>
    );
  };
  renderProjectEvents = () => {
    const events = this.getBelowMapEvents();
    if(!events.length) return null;

    return (
      <section className="page-project-details__below-section page-project-details__events" aria-labelledby="project-events-heading">
        <div className="page-project-details__below-header">
          <h2 id="project-events-heading" className="page-project-details__below-title">PROJECT EVENTS</h2>
          <SuperLink to={this.getBelowMapViewAllHref('/events')} className="page-project-details__below-view-all">View all</SuperLink>
        </div>
        <div className="page-project-details__events-list">
          {events.map(event => (
            <EventItem
              key={event.id || event.slug}
              className="page-project-details__event-item"
              disableDate
              disableDescription
              disableLocation
              event={event}
            />
          ))}
        </div>
      </section>
    );
  };
  renderProjectSignup = () => {
    return (
      <section className="page-project-details__below-section page-project-details__below-section--signup">
        <MNewsletter
          compact
          hideDescription
          hideHint
          mailchimpTag={this.props.page.mailchimpTag}
          slug={this.props.page.slug}
          tagKind="project"
          title="SIGN UP FOR PROJECT UPDATES"
        />
      </section>
    );
  };
  renderBelowMapSection = () => {
    const hasMain = this.hasBelowMapMainContent();
    const gridClass = hasMain
      ? 'page-project-details__below-grid'
      : 'page-project-details__below-grid page-project-details__below-grid--no-main';

    return (
      <div className="page-project-details__below coupling coupling--centered">
        <div className="container container--coupling">
          <div className={gridClass}>
            {hasMain ? (
              <div className="page-project-details__below-main">
                {this.renderProjectPhotos()}
                {this.renderDocuments()}
                {this.renderPartners()}
                {this.renderContactInformation()}
              </div>
            ) : null}
            <div className="page-project-details__below-aside">
              {this.renderProjectUpdates()}
              {this.renderProjectEvents()}
            </div>
            <div className="page-project-details__below-signup">
              {this.renderProjectSignup()}
            </div>
          </div>
        </div>
      </div>
    );
  };
  renderTags = (className, hideTitle) => {
    if(!this.props.page.tags || this.props.page.tags.length < 1) return null;

    let tags = this.props.page.tags.map((tag) => {
      return {
        id: tag.id,
        title: tag.title,
      };
    });
    return (
      <MListTag
        mListTag={{tags: tags}}
        hideTitle={hideTitle}
        className={`page-project-details__tags ${className || ''}`}
      />
    );
  };
  render(){
    //console.log(`render pageProjectDetail`, this.props);
    const title = apiValues.buildTitle(this.props.page?.title || 'Project');

    if(!this.props.page ||
       !this.props.page.id){
      return <DocumentTitle title={title}/>;
    }

    return (
      <>
        <DocumentTitle title={title}/>
        <PageBase>
          {this.renderBreadcrumbs()}
          <div className="page-project-details">
            {this.renderIntroSection()}
            {this.renderMapSection()}
            {this.renderBelowMapSection()}
          </div>
        </PageBase>
      </>
    );
  }
}


export default withRouter(connect(
  (state) => ({
    page: state.pageProjectDetails,
  }),
  (dispatch) => ({
    actions: bindActionCreators(pageProjectDetailActions, dispatch),
  }),
)(PageProjectDetail));


PageProjectDetail.propTypes = {
  actions: PropTypes.object.isRequired,
  history: PropTypes.object,
  latestEvents: PropTypes.array,
  latestNews: PropTypes.array,
  match: PropTypes.object.isRequired,
  page: PropTypes.object,
};
